import React, { Component, useMemo } from "react";
import {
  Accordion, Card, Button, Row,
  Form,
  Badge,
  Alert
} from "react-bootstrap";
import { Invoice } from ".././models/Invoice";
import { getDaysLeftFromDrew, formatDrewDate } from ".././utils/utils";
import { BadgeComponent } from "./Badge";


export const AccordionComponent = (props: any) => {
  // Sort invoices by due date (earliest first) for sequential selection
  const sortedInvoices = useMemo(() => {
    return [...props.invoices].sort((a: Invoice, b: Invoice) => {
      const dateA = new Date(formatDrewDate(a.dtdue));
      const dateB = new Date(formatDrewDate(b.dtdue));
      return dateA.getTime() - dateB.getTime();
    });
  }, [props.invoices]);

  // AS400 discriminates split-term policies via the case of the leading
  // character in InvoiceNumber (capital 'W' for one term, lowercase 'w' for
  // another). Sequential "pay older first" enforcement is scoped per-term
  // so a current-term installment is not gated behind a prior-term audit.
  const termKey = (invoice: Invoice): string => (invoice.InvoiceNumber ?? '').charAt(0);

  // Bounced monthly-report payments produce sibling rows: the parent (e.g.
  // 'W2026-03*') and its returned-check fee (e.g. 'W2026-03**'). They share a
  // family base, must be paid together, and count as one unit for sequencing.
  const familyBase = (invoice: Invoice): string => (invoice.InvoiceNumber ?? '').replace(/\*+$/, '');

  // All invoices in the same family as `invoice` (parent + fee siblings)
  const familyOf = (invoice: Invoice): Invoice[] => {
    const base = familyBase(invoice);
    return sortedInvoices.filter((inv: Invoice) => familyBase(inv) === base);
  };

  // Selectable invoices in the same term as the given invoice, sorted by due date
  const selectableInTerm = (invoice: Invoice): Invoice[] => {
    const t = termKey(invoice);
    return sortedInvoices.filter((inv: Invoice) => {
      const isProcessingOrScheduled = inv.Status === 'P' || inv.Status === 'S';
      return !isProcessingOrScheduled && termKey(inv) === t;
    });
  };

  // Deduplicate the selectable list by family base for sequencing purposes:
  // a family is one logical step, regardless of how many sibling rows it has.
  // Each family is represented by its earliest-due member that is not a fee
  // (no trailing '*'), falling back to the earliest row in the family.
  const selectableFamiliesInTerm = (invoice: Invoice): Invoice[] => {
    const seen = new Set<string>();
    const out: Invoice[] = [];
    for (const inv of selectableInTerm(invoice)) {
      const base = familyBase(inv);
      if (seen.has(base)) {
        continue;
      }
      seen.add(base);
      out.push(inv);
    }
    return out;
  };

  // Check if a specific invoice is selected
  const isSelected = (invoice: Invoice): boolean => {
    return (props.selectedInvoice || []).some((sel: Invoice) => sel.InvoiceNumber === invoice.InvoiceNumber);
  };

  // True if any sibling in the same family is selected (the family is "active")
  const isFamilySelected = (invoice: Invoice): boolean => {
    const base = familyBase(invoice);
    return (props.selectedInvoice || []).some((sel: Invoice) => familyBase(sel) === base);
  };

  // Within-term next-allowed family index for the term containing `invoice`
  const nextAllowedIndexInTerm = (invoice: Invoice): number => {
    const families = selectableFamiliesInTerm(invoice);
    for (let i = 0; i < families.length; i++) {
      if (!isFamilySelected(families[i])) {
        return i;
      }
    }
    return families.length;
  };

  // Within-term highest-selected family index
  const highestSelectedIndexInTerm = (invoice: Invoice): number => {
    const families = selectableFamiliesInTerm(invoice);
    let maxIdx = -1;
    for (let i = 0; i < families.length; i++) {
      if (isFamilySelected(families[i]) && i > maxIdx) {
        maxIdx = i;
      }
    }
    return maxIdx;
  };

  // Get the family index of `invoice` in the per-term family list
  const getIndex = (invoice: Invoice): number => {
    const base = familyBase(invoice);
    return selectableFamiliesInTerm(invoice).findIndex((inv: Invoice) => familyBase(inv) === base);
  };

  // Handle click - strict sequential enforcement, scoped to the invoice's
  // term and operating on whole families (parent + fee siblings).
  const handleInvoiceClick = (invoice: Invoice) => {
    const idx = getIndex(invoice);
    const familySelected = isFamilySelected(invoice);
    const family = familyOf(invoice);

    if (familySelected) {
      // DESELECT: Only allow deselecting the LAST selected family within its term
      if (idx === highestSelectedIndexInTerm(invoice)) {
        // Toggle off every currently-selected sibling
        const toToggle = family.filter((inv: Invoice) => isSelected(inv));
        if (toToggle.length > 0) {
          props.handleSelectInvoice(toToggle.length === 1 ? toToggle[0] : toToggle);
        }
      }
    } else {
      // SELECT: Only allow selecting the NEXT family in sequence within its term
      if (idx === nextAllowedIndexInTerm(invoice)) {
        // Toggle on every sibling in the family that isn't already selected
        const toToggle = family.filter((inv: Invoice) => !isSelected(inv));
        if (toToggle.length > 0) {
          props.handleSelectInvoice(toToggle.length === 1 ? toToggle[0] : toToggle);
        }
      }
    }
  };

  // Check if an invoice can be clicked (per-term sequencing, family-aware)
  const canClick = (invoice: Invoice): boolean => {
    const idx = getIndex(invoice);
    const familySelected = isFamilySelected(invoice);

    if (familySelected) {
      return idx === highestSelectedIndexInTerm(invoice);
    } else {
      return idx === nextAllowedIndexInTerm(invoice);
    }
  };

  return (
    <>
      <Accordion defaultActiveKey={sortedInvoices[0]?.InvoiceNumber?.toString()}>

        {sortedInvoices.map((invoice: Invoice) => {

          let dateDueDayLeft = getDaysLeftFromDrew(invoice.dtdue);


          let badgeColorForDueDate: any = "success";
          let badgeTextForDueDate: string = `${dateDueDayLeft} days left`;
          if (dateDueDayLeft <= 3 && dateDueDayLeft >= 0) {
            badgeColorForDueDate = "primary";
          } else if (dateDueDayLeft <= 0) {
            badgeColorForDueDate = "danger";
            badgeTextForDueDate = "Past due";
          }
          if (dateDueDayLeft === 1) {
            badgeTextForDueDate = badgeTextForDueDate.replace("days", "day");
          }

          const invoiceID = invoice.InvoiceNumber;
          const isProcessingOrScheduled = invoice.Status === 'P' || invoice.Status === 'S';
          const invoiceIndex = getIndex(invoice);
          const invoiceIsSelected = isSelected(invoice);
          const invoiceCanClick = canClick(invoice);

          // Determine why invoice is blocked (per-term sequencing)
          const isBlockedBySequence = !invoiceIsSelected && invoiceIndex > nextAllowedIndexInTerm(invoice) && !isProcessingOrScheduled;
          const isDisabled = isProcessingOrScheduled || !invoiceCanClick;

          return (
            <Accordion.Item
              key={invoiceID}
              eventKey={invoiceID}
              className={`${invoiceIsSelected ? "active" : ""} ${isDisabled && !invoiceIsSelected ? 'disabled-invoice' : ''}`}
              style={{ opacity: (isDisabled && !invoiceIsSelected) ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleInvoiceClick(invoice);
              }}
            >
              <Accordion.Header
                style={{ background: 'transparent' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInvoiceClick(invoice);
                }}
              >


                <div className="invoice_wrapper">
                  <div className="circle">
                    <div className="checkmark"></div>
                  </div>

                  <div>

                    <div className="flexbox w-100" style={{ textAlign: 'left' }}>
                      <span>     Invoice #{invoice.InvoiceNumber}
                        <BadgeComponent style={{ marginLeft: '10px', display: 'inline', fontWeight: '300', padding: '7px ​14px 7px 10px' }} variant={badgeColorForDueDate.toString()} date={formatDrewDate(invoice.dtdue)} badgeTextForDueDate={badgeTextForDueDate} pill={true} />
                        {isProcessingOrScheduled && (
                          <Badge bg="secondary" style={{ marginLeft: '10px' }}>
                            {invoice.Status === 'P' ? 'Processing' : 'Scheduled'}
                          </Badge>
                        )}
                        {isBlockedBySequence && (
                          <Badge bg="warning" text="dark" style={{ marginLeft: '10px' }}>
                            Select previous first
                          </Badge>
                        )}
                      </span>
                    </div>

                    <span style={{ fontFamily: 'monospace', display: 'flex', marginTop: '5px', fontSize: '34px', color: '#000000' }}>
                      <span style={{ fontSize: '34px', color: '#bbbbbb' }}>$</span>
                      {parseFloat(invoice.amount).toFixed(2)}</span>
                    {isProcessingOrScheduled && (
                      <small className="text-muted" style={{ display: 'block', marginTop: '6px' }}>
                        Payment is already being processed. Please wait for completion before retrying.
                      </small>
                    )}
                    {isBlockedBySequence && (
                      <small className="text-muted" style={{ display: 'block', marginTop: '6px' }}>
                        You must select the previous invoice before selecting this one.
                      </small>
                    )}
                  </div>
                </div>





              </Accordion.Header>
            </Accordion.Item>);
        })}

      </Accordion >




      {(props.selectedInvoice?.length ?? 0) >= 1 && (
        <div className="total-notifier">
          Your total: ${props.selectedInvoice?.reduce((acc: number, invoice: Invoice) => acc + parseFloat(invoice.amount), 0).toFixed(2)}
        </div>
        )}


      <div className="flexbox">

        <Button
          variant="primary"
          type="submit"
          className="mt-3 float-right"
          onClick={props.handleshowResult}
          disabled={!props.selectedInvoice || props.selectedInvoice.length === 0}
        >
          Continue
        </Button>
      </div>

      {(!props.selectedInvoice || props.selectedInvoice.length === 0) && (
        <div className="text-muted" style={{ marginTop: '10px', textAlign: 'center' }}>
          Select an eligible invoice to continue.
        </div>
      )}

    </>
  );
};
